// src/App.jsx
import React, { Suspense, lazy, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import store from './store';
import NewLoginPageV2 from './NewLoginPageV2';
import LandingPage from './pages/LandingPage';
import ProjectLoginPage from './pages/ProjectLoginPage';
import DrawerAppBar from './components/DrawerAppBar';
import NotFoundPage from './pages/NotFoundPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminHomePage from './pages/SuperAdminHomePage';
import { AdminPanelThemeProvider } from './project-modules/store/AdminPanelThemeContext';

// Тяжёлые страницы — lazy для уменьшения initial bundle.
// См. docs/07-frontend/refactors/QUEUE.md #04 и SUPERADMIN_MODULARIZATION.md.
const MoreDetails = lazy(() => import('./pages/MoreDetails'));
const TrendsPage = lazy(() => import('./pages/TrendsPage'));
const AlarmsPage = lazy(() => import('./pages/AlarmsPage'));
const SupportPageV2 = lazy(() => import('./pages/SupportPageV2'));
const SupportProjectMetricsPage = lazy(() => import('./pages/SupportProjectMetricsPage'));
const SuperAdminPageV2ProjectMvp = lazy(() => import('./pages/SuperAdminPageV2ProjectMvp'));
const ModuleStorePage = lazy(() => import('./pages/ModuleStorePage'));
const UidManagementPageV2 = lazy(() => import('./pages/UidManagementPageV2'));
const HelpCenterPage = lazy(() => import('./pages/help/HelpCenterPage'));
const PublicExamplePage = lazy(() => import('./pages/examples/PublicExamplePage'));
const UserSetsPageProjectCards = lazy(() => import('./pages/UserSetsPageProjectCards'));
const GroupsPageProjectCards = lazy(() => import('./pages/GroupsPageProjectCards'));


import ManifestLink from './components/ManifestLink';
import { controller } from './api/controller';
import {
  ENTERPRISE_MODE,
  isEnterpriseModeEnabledFromConfig,
} from './constants/featureFlags';
import { DisplayModeProvider } from './contexts/DisplayModeContext';
import { MobileThemeProvider } from './contexts/MobileThemeContext';
import { TimeRangeProvider } from './contexts/TimeRangeContext';
import { TrendsSettingsProvider } from './contexts/TrendsSettingsContext';
import { AlarmsSettingsProvider } from './contexts/AlarmsSettingsContext';
import { PublicUiLocaleProvider } from './contexts/PublicUiLocaleContext';
import { ThemeProvider } from '@mui/material/styles';
import { appTheme } from './theme/appTheme';

// Селектор для LoginRedirect
const selectAuthState = createSelector(
  [state => state.authSlice],
  (authSlice) => ({
    result: authSlice?.result ?? false,
    role: authSlice?.role ?? null,
  })
);

function LoginRedirect({ children }) {
  const authState = useSelector(selectAuthState);
  if (authState.result) {
    const projectSlug = getProjectSlugFromLocation();
    
    // В проектном контексте - редирект по роли
    if (projectSlug) {
      const redirectPath = authState.role === 'superadmin'
        ? '/superadmin'
        : authState.role === 'support'
          ? '/support'
          : authState.role === 'admin'
            ? `/p/${projectSlug}/admin`
            : `/p/${projectSlug}/groups`;
      return <Navigate to={redirectPath} replace />;
    }
    
    // В глобальном контексте - редирект по роли
    if (authState.role === 'superadmin') {
      return <Navigate to="/v2/superadmin" replace />;
    }
    if (authState.role === 'support') {
      return <Navigate to="/support" replace />;
    }
    
    // Для admin/user - остаемся на логине (они должны заходить через /p/:slug/login)
    return children;
  }
  return children;
}

function RootPublicPage() {
  const [enterpriseModeEnabled, setEnterpriseModeEnabled] = useState(
    ENTERPRISE_MODE,
  );
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    let mounted = true;
    controller
      .getPublicAuthConfig()
      .then((cfg) => {
        if (!mounted) return;
        setEnterpriseModeEnabled(isEnterpriseModeEnabledFromConfig(cfg));
      })
      .catch(() => {
        if (!mounted) return;
        setEnterpriseModeEnabled(ENTERPRISE_MODE);
      })
      .finally(() => {
        if (mounted) setIsResolved(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!isResolved) {
    return <div>Loading...</div>;
  }

  if (enterpriseModeEnabled) {
    return <Navigate to="/v2/login" replace />;
  }

  return <LandingPage />;
}

LoginRedirect.propTypes = {
  children: PropTypes.node,
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: 'center', padding: '24px', backgroundColor: '#0a1a2f' }}>
          <h2 style={{ color: 'red', marginBottom: '16px' }}>Произошла ошибка</h2>
          <p style={{ color: '#b0bec5', marginBottom: '16px' }}>{this.state.error?.message || 'Неизвестная ошибка'}</p>
          <button
            style={{ backgroundColor: '#1976d2', color: '#fff', padding: '8px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            onClick={() => window.location.reload()}
          >
            Перезагрузить
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
};

// Компонент для условного рендера AppBar
function AppLayout({ children }) {
  const location = useLocation();
  const isProjectPage = location.pathname.startsWith('/p/');
  const isProjectLogin = /^\/p\/[^/]+\/login$/.test(location.pathname);
  // На /p/:slug/admin и /p/:slug/superadmin страница SuperAdminPageV2ProjectMvp
  // уже рисует собственный header (project picker, logout, language). Глобальный
  // DrawerAppBar там даёт дубль — поэтому скрываем его на этих путях.
  // Поддерживаем как точные маршруты, так и query string (?tab=…).
  const isSuperAdminPanel =
    /^\/p\/[^/]+\/(?:admin|superadmin)\/?$/.test(location.pathname);
  const isModuleStore = /^\/p\/[^/]+\/store\/?$/.test(location.pathname);
  const showDrawerAppBar = isProjectPage && !isProjectLogin && !isSuperAdminPanel && !isModuleStore;

  return (
    <>
      <ManifestLink />
      {showDrawerAppBar && <DrawerAppBar />}
      <div style={{ 
        paddingTop: showDrawerAppBar ? '64px' : '0', 
        height: '100vh', 
        boxSizing: 'border-box' 
      }}>
        {children}
      </div>
    </>
  );
}

AppLayout.propTypes = {
  children: PropTypes.node,
};

function RedirectProjectSubpath({ toSuffix }) {
  const { slug } = useParams();
  return <Navigate to={`/p/${slug}/${toSuffix}`} replace />;
}

function RedirectAdminLegacy() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const tail = String(pathname || '').split('/').filter(Boolean).pop();
  const params = new URLSearchParams();
  if (tail === 'stations') params.set('tab', 'device');
  else if (tail === 'users') params.set('tab', 'users');
  else if (tail === 'sets') {
    params.set('tab', 'layout');
    params.set('sub', 'sets');
  } else if (tail === 'groups') {
    params.set('tab', 'layout');
    params.set('sub', 'groups');
  } else {
    params.set('tab', 'project');
  }
  return <Navigate to={`/p/${slug}/admin?${params.toString()}`} replace />;
}

RedirectProjectSubpath.propTypes = {
  toSuffix: PropTypes.string.isRequired,
};

RedirectAdminLegacy.propTypes = {};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={appTheme}>
        <DisplayModeProvider>
          <MobileThemeProvider>
            <TimeRangeProvider>
              <TrendsSettingsProvider>
                <AlarmsSettingsProvider>
            <div className="App">
            <Router basename="/">
              <PublicUiLocaleProvider>
              <ErrorBoundary>
                <AppLayout>
                  <Suspense fallback={<div style={{ padding: 24, color: '#b0bec5' }}>Loading...</div>}>
                  <Routes>
              {/* Публичный лендинг + отдельный маршрут логина */}
              <Route path="/" element={<RootPublicPage />} />
              <Route path="/examples" element={<Navigate to="/examples/plant" replace />} />
              <Route path="/examples/:kind" element={<PublicExamplePage />} />
              <Route path="/login" element={<Navigate to="/v2/login" replace />} />

              {/* V2 роуты */}
              <Route path="/v2/login" element={<NewLoginPageV2 />} />
              <Route path="/v2/verify-email" element={<VerifyEmailPage />} />
              <Route path="/v2/support" element={<ProtectedRoute supportOnly><SupportPageV2 /></ProtectedRoute>} />
              <Route path="/v2/support/project/:slug" element={<ProtectedRoute supportOnly><SupportProjectMetricsPage /></ProtectedRoute>} />
              <Route
                path="/v2/superadmin"
                element={(
                  <ProtectedRoute superAdminOnly>
                    <AdminPanelThemeProvider>
                      <SuperAdminHomePage />
                    </AdminPanelThemeProvider>
                  </ProtectedRoute>
                )}
              />
              <Route path="/v2/superadmin/project/:slug" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              {['/v2/superadmin/help', '/v2/superadmin/help/:section'].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={(
                    <ProtectedRoute superAdminOnly>
                      <AdminPanelThemeProvider>
                        <HelpCenterPage />
                      </AdminPanelThemeProvider>
                    </ProtectedRoute>
                  )}
                />
              ))}
              <Route path="/v2/superadmin/examples" element={<Navigate to="/v2/superadmin/help/example-plant" replace />} />
              <Route path="/v2/superadmin/uid/:uidId" element={<ProtectedRoute superAdminOnly><UidManagementPageV2 /></ProtectedRoute>} />
              
              {/* Legacy: старый URL кабинета → workspace первого проекта или home */}
              <Route path="/v2/superadmin/workspace" element={<ProtectedRoute superAdminOnly><Navigate to="/v2/superadmin" replace /></ProtectedRoute>} />
              
              {/* Глобальные маршруты (без project slug) */}
              <Route path="/superadmin" element={<ProtectedRoute superAdminOnly><Navigate to="/v2/superadmin" replace /></ProtectedRoute>} />
              <Route path="/support" element={<ProtectedRoute supportOnly><SupportPageV2 /></ProtectedRoute>} />
              {/* Legacy paths: страница теперь сама нормализует их в ?tab=&sub= */}
              <Route path="/superadmin/alarm-rules" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/superadmin/trend-configs" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/superadmin/code-groups" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/superadmin/command-templates" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/p/:slug/command-templates" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/superadmin/card-builder" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/p/:slug/card-builder" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/superadmin/component-builder" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              <Route path="/p/:slug/component-builder" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              
              {/* Проектные маршруты: /p/:slug/* */}
              <Route path="/p/:slug/login" element={<ProjectLoginPage />} />
              <Route path="/p/:slug/superadmin" element={<ProtectedRoute superAdminOnly><SuperAdminPageV2ProjectMvp scope="catalog" /></ProtectedRoute>} />
              {/* Единая project-scoped панель для admin и superadmin */}
              <Route path="/p/:slug/admin" element={<ProtectedRoute projectAdminPanel><SuperAdminPageV2ProjectMvp scope="project" /></ProtectedRoute>} />
              <Route path="/p/:slug/store" element={<ProtectedRoute projectStoreAccess><ModuleStorePage /></ProtectedRoute>} />
              <Route path="/p/:slug/project-card/:cardName" element={<ProtectedRoute><MoreDetails /></ProtectedRoute>} />
              <Route path="/p/:slug/details/:id" element={<ProtectedRoute><MoreDetails /></ProtectedRoute>} />
              <Route path="/p/:slug/trends/:id" element={<ProtectedRoute><TrendsPage /></ProtectedRoute>} />
              <Route path="/p/:slug/alarms/:id" element={<ProtectedRoute><AlarmsPage /></ProtectedRoute>} />
              <Route path="/p/:slug/alarms-v2" element={<ProtectedRoute><AlarmsPage /></ProtectedRoute>} />
              <Route path="/p/:slug/alarms-v2/:id" element={<ProtectedRoute><AlarmsPage /></ProtectedRoute>} />
              {/* Legacy admin paths → единая панель /admin?tab=... */}
              <Route path="/p/:slug/admin/users" element={<ProtectedRoute adminOnly><RedirectAdminLegacy /></ProtectedRoute>} />
              <Route path="/p/:slug/admin/sets" element={<ProtectedRoute adminOnly><RedirectAdminLegacy /></ProtectedRoute>} />
              <Route path="/p/:slug/admin/stations" element={<ProtectedRoute adminOnly><RedirectAdminLegacy /></ProtectedRoute>} />
              <Route path="/p/:slug/admin/groups" element={<ProtectedRoute adminOnly><RedirectAdminLegacy /></ProtectedRoute>} />
              <Route path="/p/:slug/sets" element={<ProtectedRoute><UserSetsPageProjectCards /></ProtectedRoute>} />
              <Route path="/p/:slug/groups" element={<ProtectedRoute><GroupsPageProjectCards /></ProtectedRoute>} />
              <Route path="/p/:slug/groups/:groupId" element={<ProtectedRoute><GroupsPageProjectCards /></ProtectedRoute>} />
              <Route path="/p/:slug/svg-runtime" element={<ProtectedRoute><RedirectProjectSubpath toSuffix="groups" /></ProtectedRoute>} />
              {/* Удалены отдельные страницы; старый URL ведёт в группы */}
              <Route path="/p/:slug/user/panel" element={<ProtectedRoute><RedirectProjectSubpath toSuffix="groups" /></ProtectedRoute>} />
              <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  </Suspense>
                </AppLayout>
              </ErrorBoundary>
              </PublicUiLocaleProvider>
            </Router>
                </div>
                </AlarmsSettingsProvider>
              </TrendsSettingsProvider>
            </TimeRangeProvider>
          </MobileThemeProvider>
        </DisplayModeProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;

function getProjectSlugFromLocation() {
  try {
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const parts = path.split('/').filter(Boolean);
    if (parts.length >= 2 && parts[0] === 'p') return parts[1];
  } catch (_e) {
    // ignore
  }
  return '';
}
